import { anyOf, arrayOf, is, objectOf } from "@altostra/type-validations";
import { boolean, nullOrUndefined, number, string } from "@altostra/type-validations/lib/primitives";

import {
  callWebhookToolType,
  getCustomAttributeToolType,
  getWebhookResponseToolType,
  knowledgeSourceToolType,
  logCustomEventToolType,
  setCustomAttributeToolType,
  workflowAttributeGetterToolType,
  workflowAttributeSetterToolType,
} from "./types";

const nonEmptyString = (value: unknown): value is string => typeof value === "string" && value.length > 0;

export const isKnowledgeSourceToolConfig = objectOf({
  tool_type: is(knowledgeSourceToolType),
  definition_id: nonEmptyString,
  excluded_fields: arrayOf(string),
});

export const isWorkflowAttributeSetterToolConfig = objectOf({
  tool_type: is(workflowAttributeSetterToolType),
  definition_id: nonEmptyString,
});

export const isWorkflowAttributeGetterToolConfig = objectOf({
  tool_type: is(workflowAttributeGetterToolType),
  definition_id: nonEmptyString,
});

export const isLogCustomEventProperty = objectOf({
  name: nonEmptyString,
  source: anyOf(is("agent"), is("workflow_attribute"), is("custom_attribute")),
  source_id: anyOf(nullOrUndefined, string),
  description: anyOf(nullOrUndefined, string),
  required: boolean,
});

export const isLogCustomEventToolConfig = objectOf({
  tool_type: is(logCustomEventToolType),
  marker_id: nonEmptyString,
  event_name: nonEmptyString,
  description: nonEmptyString,
  properties: arrayOf(isLogCustomEventProperty),
});

export const isSetCustomAttributeToolConfig = objectOf({
  tool_type: is(setCustomAttributeToolType),
  marker_id: nonEmptyString,
  attribute_name: nonEmptyString,
  description: nonEmptyString,
  source: anyOf(is("agent"), is("workflow_attribute")),
  source_id: anyOf(nullOrUndefined, string),
});

export const isGetCustomAttributeToolConfig = objectOf({
  tool_type: is(getCustomAttributeToolType),
  marker_id: nonEmptyString,
  attribute_name: nonEmptyString,
});

export const isCallWebhookToolConfig = objectOf({
  tool_type: is(callWebhookToolType),
  definition_id: nonEmptyString,
});

export const isGetWebhookResponseToolConfig = objectOf({
  tool_type: is(getWebhookResponseToolType),
  marker_id: nonEmptyString,
  definition_id: nonEmptyString,
  response_field_name: nonEmptyString,
});

export const isToolConfig = anyOf(
  isKnowledgeSourceToolConfig,
  isWorkflowAttributeSetterToolConfig,
  isWorkflowAttributeGetterToolConfig,
  isLogCustomEventToolConfig,
  isSetCustomAttributeToolConfig,
  isGetCustomAttributeToolConfig,
  isCallWebhookToolConfig,
  isGetWebhookResponseToolConfig
);

export const isConversationalWorkflowStep = objectOf({
  instruction: string,
  tool_configs: arrayOf(isToolConfig),
});

export const isConversationalWorkflowChatSetting = objectOf({
  app_ids: arrayOf(string),
});

export const isConversationalWorkflowMessagingChannelSetting = objectOf({
  subscription_group_ids: arrayOf(string),
});

export const isConversationalWorkflowResultObject = objectOf({
  id: string,
  name: string,
  purpose: string,
  segment_ids: arrayOf(string),
  steps: arrayOf(isConversationalWorkflowStep),
  chat_setting: anyOf(nullOrUndefined, isConversationalWorkflowChatSetting),
  messaging_channel_setting: anyOf(nullOrUndefined, isConversationalWorkflowMessagingChannelSetting),
  created_by_email: string,
  created_at: string,
  updated_at: string,
});

export const isConversationalWorkflowResult = objectOf({
  result: isConversationalWorkflowResultObject,
});

export const isOperatorSafeConversationalWorkflowStep = objectOf({
  instruction: string,
});

export const isOperatorSafeConversationalWorkflowResultObject = objectOf({
  id: string,
  name: string,
  purpose: string,
  step_count: number,
  steps: arrayOf(isOperatorSafeConversationalWorkflowStep),
});

export const isOperatorSafeConversationalWorkflowResult = objectOf({
  result: isOperatorSafeConversationalWorkflowResultObject,
});

export const isGetConversationalWorkflowResult = anyOf(
  isConversationalWorkflowResult,
  isOperatorSafeConversationalWorkflowResult
);
